
import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ManualExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ManualExportDialog = ({ open, onOpenChange }: ManualExportDialogProps) => {
  // Functie om eventuele achtergebleven aria-hidden of style attributen op te ruimen
  const cleanupDOMAfterClose = () => {
    // Verwijder eventuele aria-hidden attributen die door Radix UI zijn toegevoegd
    document.querySelectorAll('[aria-hidden="true"]').forEach(el => {
      if (el instanceof HTMLElement && !el.dataset.permanentAriaHidden) {
        el.removeAttribute('aria-hidden');
      }
    });
    
    // Herstel overflow op body en html
    document.body.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('overflow');
    
    // Verwijder eventuele inert attributen
    document.querySelectorAll('[inert]').forEach(el => {
      if (el instanceof HTMLElement) {
        el.removeAttribute('inert');
      }
    });

    // Verwijder data-radix-scroll-lock attributen
    document.querySelectorAll('[data-radix-scroll-lock]').forEach(el => {
      if (el instanceof HTMLElement) {
        el.removeAttribute('data-radix-scroll-lock');
      }
    });

    // Verwijder eventuele achtergebleven overlay elementen
    document.querySelectorAll('.fixed.inset-0').forEach(el => {
      if (el instanceof HTMLElement && 
          el.getAttribute('role') === 'presentation' &&
          !el.children.length) {
        el.remove();
      }
    });

    // Verwijder Radix focus scope elementen
    document.querySelectorAll('[data-radix-focus-guard]').forEach(el => {
      el.remove();
    });

    // Verwijder eventuele pointer-events: none van body of main container
    document.querySelectorAll('body, #root, #app, main').forEach(el => {
      if (el instanceof HTMLElement) {
        el.style.pointerEvents = '';
      }
    });
  };

  // Voer cleanup uit bij unmount van de component
  useEffect(() => {
    return () => {
      cleanupDOMAfterClose();
    };
  }, []);
  
  // Zorg ervoor dat cleanup altijd wordt uitgevoerd wanneer de dialog sluit
  const handleOpenChange = (newOpenState: boolean) => {
    onOpenChange(newOpenState);
    if (!newOpenState) {
      // Directe cleanup
      cleanupDOMAfterClose();
      // En nog een keer na een korte timeout om er zeker van te zijn dat Radix UI's eigen cleanup voltooid is
      setTimeout(() => {
        cleanupDOMAfterClose();
      }, 100);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          // Voorkom dat de focus wordt vastgehouden of verkeerd wordt gezet
          event.preventDefault();
          // Zorg dat eventuele portals correct worden opgeruimd
          cleanupDOMAfterClose();
        }}
        onEscapeKeyDown={() => {
          // Extra cleanup bij ESC toets
          setTimeout(cleanupDOMAfterClose, 10);
        }}
        onInteractOutside={(event) => {
          // Voorkom dat interacties buiten de dialog worden geblokkeerd
          event.preventDefault();
          // Sluit dialog en ruim op
          handleOpenChange(false);
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>🪄</span> Mooi werk, coach! Tijd om je plattegrond te bewaren.
          </DialogTitle>
          <DialogDescription>
            Je hebt net iets waardevols gebouwd – en soms is een screenshot gewoon de slimste (en eerlijk gezegd: de meest betrouwbare) manier om dat te delen.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground mb-2">
            ⚠️ De andere exportfuncties zijn nog in ontwikkeling en in bèta test release. 
            Daarom raden we deze methode aan als je zeker wilt zijn van een goed resultaat.
          </div>
          
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span>🔍</span>
              <span><strong>Stap 1:</strong> Zoom je browser iets uit (bijv. 80%)</span>
            </div>
            
            <div className="flex items-start gap-2">
              <span>📸</span>
              <span><strong>Stap 2:</strong> Maak een screenshot met de sneltoets:</span>
            </div>
            
            <div className="ml-8 space-y-2">
              <div className="flex gap-2">
                <span className="font-bold">Mac:</span>
                <code className="bg-muted px-2 py-0.5 rounded">Shift + Command + 4</code>
              </div>
              
              <div className="flex gap-2">
                <span className="font-bold">Windows:</span>
                <code className="bg-muted px-2 py-0.5 rounded">Windows + Shift + S</code>
              </div>
            </div>
          </div>

          <div className="text-sm space-y-2 mt-2">
            <div>💾 Daarna kun je de afbeelding eenvoudig:</div>
            <ul className="list-disc list-inside">
              <li>opslaan op je computer</li>
              <li>plakken in OneNote, Word of je lesmap</li>
              <li>of snel delen met collega's</li>
            </ul>
          </div>

          <div className="text-sm text-muted-foreground mt-2">
            🚀 <strong>Extra tip:</strong> Upload je screenshot op <a href="https://turnlesmaker.nl" target="_blank" className="underline">turnlesmaker.nl</a> en bouw er direct een volledige turnles omheen. 
            Zo wordt je plattegrond het startpunt van een complete voorbereiding.
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <DialogClose asChild>
            <Button onClick={() => {
              cleanupDOMAfterClose();
              setTimeout(cleanupDOMAfterClose, 50);
              setTimeout(cleanupDOMAfterClose, 100);
            }}>Sluiten</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManualExportDialog;
